import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React from "react";
import { Platform } from "react-native";
import { useTranslation } from "react-i18next";
import { isElectronRunning } from "../../../App";
import Joyride from "react-joyride";

const SingleStepTooltip = ({
  step,
  closeProps,
  primaryProps,
  tooltipProps,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const primary = currentTheme?.theme?.primary;
  return (
    <div
      {...tooltipProps}
      style={{
        backgroundColor: currentTheme?.theme?.backgroundColor,
        borderRadius: 12,
        padding: 16,
        maxWidth: 360,
        boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        fontFamily: "normalFont",
      }}
    >
      <div
        style={{
          color: currentTheme?.theme?.font_color,
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        {step.content}
      </div>
      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          {...closeProps}
          style={{
            border: "none",
            background: "transparent",
            color: currentTheme?.theme?.font_color,
            fontFamily: "normalFont",
            fontSize: 14,
            cursor: "pointer",
            padding: "6px 12px",
          }}
        >
          {t("common.close")}
        </button>
        <button
          {...primaryProps}
          style={{
            backgroundColor: primary,
            color: currentTheme?.theme?.black || "#fff",
            border: "none",
            borderRadius: 6,
            fontFamily: "normalFont",
            fontSize: 14,
            cursor: "pointer",
            padding: "8px 14px",
          }}
        >
          {t("common.doNotShowAgain")}
        </button>
      </div>
    </div>
  );
};

const AppJoyRide = ({
  steps,
  run,
  handleCallback,
  styles = {
    beaconInner: {},
    beaconOuter: {},
  },
  ...props
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();

  const isSupported = Platform.OS === "web" || isElectronRunning();
  const isSingleStep = steps?.length === 1;

  const enhancedCallback = (data) => {
    if (data.type === "error:target_not_found") {
      console.warn("Joyride: Target not found for step", data.index, data.step?.target);
    }
    if (handleCallback) {
      handleCallback(data);
    }
  };

  if (!isSupported || !steps?.length) return null;

  return (
    <Joyride
      callback={enhancedCallback}
      continuous
      disableOverlay={false}
      disableOverlayClose
      hideCloseButton
      run={run}
      scrollToFirstStep
      showProgress={!isSingleStep}
      showSkipButton={!isSingleStep}
      steps={steps}
      tooltipComponent={isSingleStep ? SingleStepTooltip : undefined}
      locale={
        isSingleStep
          ? {
              last: t("common.doNotShowAgain"),
              next: t("common.doNotShowAgain"),
              skip: t("common.close"),
            }
          : undefined
      }
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: currentTheme?.theme?.primary,
          backgroundColor: currentTheme?.theme?.backgroundColor,
          arrowColor: currentTheme?.theme?.backgroundColor,
          overlayColor: "rgba(0,0,0,0.55)",
        },
        beacon: {
          accentColor: currentTheme?.theme?.primary,
        },
        beaconInner: {
          marginTop: 2,
          marginRight: 2,
          ...styles?.beaconInner,
        },
        beaconOuter: {
          top: 2,
          right: 2,
          ...styles?.beaconOuter,
        },
        spotlight: {
          borderRadius: 10,
        },
        tooltipContent: {
          fontFamily: "normalFont",
          color: currentTheme?.theme?.font_color,
          fontSize: 14,
        },
        tooltipContainer: {
          backgroundColor: currentTheme?.theme?.backgroundColor,
          borderRadius: 12,
        },
        buttonNext: {
          backgroundColor: currentTheme?.theme?.primary,
          fontFamily: "normalFont",
          fontSize: 14,
        },
        buttonBack: {
          fontFamily: "normalFont",
          color: currentTheme?.theme?.primary,
          fontSize: 14,
        },
        buttonSkip: {
          fontFamily: "normalFont",
          color: currentTheme?.theme?.font_color,
          fontSize: 14,
        },
        tooltipFooter: {
          marginTop: isSingleStep ? 8 : 0,
        },
      }}
      {...props}
    />
  );
};

export default AppJoyRide;